import { useCallback, useEffect, useMemo, useState } from 'react';
import type { BalanceTimelineResponse } from '@tv-casino/shared';
import { fetchBalanceTimeline } from '../lib/casinoApi.js';
import { usePoll } from '../lib/usePoll.js';
import { balanceChartModel } from '@tv-casino/viewmodels/components/balanceChartModel.js';
import { SparkLegend, Sparkline } from './Sparkline.js';
import styles from './BalanceChart.module.css';

/** The tape re-reads while the tab is open, so the night stays live. */
export const POLL_MS = 5000;

/**
 * Every member's night as its own market-desk sparkline, for the owner
 * console's lineup. The server has already collapsed each round's stake and
 * payout into a single end-of-round point, so what reads as a dip is a round
 * that actually cost chips — the shape of the night, not the sawtooth of
 * betting. A failed read costs the card, never the page.
 */
export function BalanceChart({ casinoCode }: { casinoCode: string }) {
  const [timeline, setTimeline] = useState<BalanceTimelineResponse | null>(null);
  const [failed, setFailed] = useState(false);

  /**
   * One read of the tape. The first read may cost the card; polls only ever
   * refresh it — a missed tick keeps the lines on screen, and a landing one
   * brings a hidden card back.
   */
  const read = useCallback(
    async (first = false) => {
      try {
        setTimeline(await fetchBalanceTimeline(casinoCode));
        setFailed(false);
      } catch {
        if (first) setFailed(true);
      }
    },
    [casinoCode],
  );

  useEffect(() => {
    setTimeline(null);
    setFailed(false);
    void read(true);
  }, [read]);

  usePoll(() => void read(), POLL_MS);

  const vm = useMemo(() => balanceChartModel(timeline), [timeline]);

  if (failed) return null;

  const sessionTone =
    vm.session?.changeTone === 'win'
      ? styles.win
      : vm.session?.changeTone === 'loss'
        ? styles.loss
        : undefined;

  return (
    <section className={styles.panel} aria-label="The night in chips">
      <p className={styles.title}>Session tape</p>
      {!vm.ready ? (
        <p className={styles.dim}>Reading the ledger…</p>
      ) : vm.emptyLine ? (
        <p className={styles.dim}>{vm.emptyLine}</p>
      ) : (
        <>
          {vm.session && (
            <dl className={styles.session}>
              <div>
                <dt>Open</dt>
                <dd>{vm.session.openLabel}</dd>
              </div>
              <div>
                <dt>High</dt>
                <dd>{vm.session.highLabel}</dd>
              </div>
              <div>
                <dt>Low</dt>
                <dd>{vm.session.lowLabel}</dd>
              </div>
              <div>
                <dt>Last</dt>
                <dd>{vm.session.lastLabel}</dd>
              </div>
              <div>
                <dt>Chg</dt>
                <dd className={sessionTone}>
                  {vm.session.changeLabel} {vm.session.changePctLabel}
                </dd>
              </div>
              <div>
                <dt>Book</dt>
                <dd>{vm.session.membersLabel}</dd>
              </div>
            </dl>
          )}
          <ul className={styles.legend}>
            {vm.lines.map((line) => (
              <li key={line.memberId} className={styles.member}>
                <p className={styles.legendRow}>
                  <span className={styles.legendName}>{line.name}</span>
                  <span className={styles.legendMeta}>
                    {line.currentLabel} now · {line.changeLabel} {line.changePctLabel} · peak{' '}
                    {line.peakLabel} · low {line.troughLabel}
                  </span>
                </p>
                <Sparkline
                  points={line.points}
                  runs={line.runs}
                  marks={line.marks}
                  single={line.single}
                  label={`${line.name} balance line`}
                />
              </li>
            ))}
          </ul>
          <SparkLegend items={vm.legend} />
        </>
      )}
    </section>
  );
}
